import { axiosInstance } from 'src/utils/axios';
import { STORAGE_KEY_ACCESS, STORAGE_KEY_REFRESH } from 'src/config/storage-keys';

// Re-exportar as constantes para manter compatibilidade
export { STORAGE_KEY_ACCESS, STORAGE_KEY_REFRESH };

// ----------------------------------------------------------------------

export function jwtDecode(token: string) {
  try {
    if (!token || typeof token !== 'string') {
      throw new Error('Invalid token format');
    }

    const base64Url = token.split('.')[1];
    if (!base64Url) {
      throw new Error('Invalid token format');
    }

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode JWT token:', error);
    throw new Error('Invalid token');
  }
}

// ----------------------------------------------------------------------

export const isValidToken = (accessToken: string) => {
  if (!accessToken) {
    return false;
  }

  try {
    const decoded = jwtDecode(accessToken);
    const currentTime = Date.now() / 1000;

    return decoded.exp && decoded.exp > currentTime;
  } catch (error) {
    console.error('Token validation failed:', error);
    return false;
  }
};

// ----------------------------------------------------------------------

// Função para gerar um token JWT mock válido para desenvolvimento
export const generateMockToken = (expirationHours = 24) => {
  // Base64 encode do header
  const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));

  // Payload com exp (expiration time) válido
  const now = Math.floor(Date.now() / 1000); // timestamp atual em segundos
  const exp = now + expirationHours * 60 * 60; // expira em X horas

  const payload = btoa(
    JSON.stringify({
      sub: 'user123',
      email: 'user@test.com',
      iat: now,
      exp,
    })
  );

  // Signature mock (em produção seria assinado com secret)
  const signature = 'mock_signature_for_testing';

  return `${header}.${payload}.${signature}`;
};

// ----------------------------------------------------------------------

// Array com todas as instâncias axios para aplicar tokens
const axiosInstances = [axiosInstance];

// Função para aplicar token em todas as instâncias
const setTokenInAllInstances = (token: string | null) => {
  axiosInstances.forEach((instance) => {
    if (token) {
      instance.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      delete instance.defaults.headers.common.Authorization;
    }
  });
};

export const setSession = (accessToken: string | null, refreshToken: string | null) => {
  if (accessToken) {
    localStorage.setItem(STORAGE_KEY_ACCESS, accessToken);
    localStorage.setItem(STORAGE_KEY_REFRESH, refreshToken || '');
    setTokenInAllInstances(accessToken);
  } else {
    localStorage.removeItem(STORAGE_KEY_ACCESS);
    localStorage.removeItem(STORAGE_KEY_REFRESH);
    setTokenInAllInstances(null);
  }
};

// ----------------------------------------------------------------------

export const clearSession = () => {
  setSession(null, null);
};

// ----------------------------------------------------------------------

export const getStoredTokens = () => ({
  accessToken: localStorage.getItem(STORAGE_KEY_ACCESS),
  refreshToken: localStorage.getItem(STORAGE_KEY_REFRESH),
});
